import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import StepProgress from '../../components/StepProgress';
import FormField from '../../components/FormField';

const frameworkIds = ['eidas', 'nist', 'toip', 'custom'] as const;
const orgTypeKeys = ['Employment', 'Education', 'Healthcare', 'Financial', 'Government', 'General'];

export default function VerifierOnboarding() {
  const [step, setStep] = useState(0);
  const [org, setOrg] = useState({ name: '', type: '', website: '', purpose: '' });
  const [selectedFramework, setSelectedFramework] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const { t } = useTranslation(['verifier', 'common']);

  const generatedDid = 'did:adi:verifier:' + org.name.toLowerCase().replace(/\s+/g, '-').slice(0, 10) + '-' + Math.random().toString(36).slice(2, 8);

  const steps = [
    { label: t('onboarding.steps.org.label'), description: t('onboarding.steps.org.description') },
    { label: t('onboarding.steps.did.label'), description: t('onboarding.steps.did.description') },
    { label: t('onboarding.steps.framework.label'), description: t('onboarding.steps.framework.description') },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('onboarding.title')}</h1>
        <p className="mt-1 text-sm text-text-muted">{t('onboarding.subtitle')}</p>
      </div>

      <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
        <StepProgress steps={steps} currentStep={submitted ? 3 : step} />
      </div>

      <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
        {submitted ? (
          <div className="py-8 text-center">
            <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-emerald-50">
              <span className="material-symbols-outlined text-emerald-500" style={{ fontSize: 32 }}>check_circle</span>
            </div>
            <h2 className="text-lg font-bold text-text-main">{t('onboarding.submitted.title')}</h2>
            <p className="mt-2 text-sm text-text-muted">{t('onboarding.submitted.subtitle')}</p>
            <div className="mx-auto mt-6 max-w-sm rounded-lg border border-border-subtle bg-slate-50 p-4 text-left">
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-text-faint">{t('onboarding.submitted.organization')}</span>
                  <span className="font-bold text-text-main">{org.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-faint">{t('onboarding.submitted.did')}</span>
                  <span className="font-mono font-bold text-primary">{generatedDid}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-faint">{t('onboarding.submitted.framework')}</span>
                  <span className="font-bold text-text-main">{selectedFramework ? t(`onboarding.frameworks.${selectedFramework}.name`) : ''}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-faint">{t('onboarding.submitted.status')}</span>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-100/50 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700">
                    <span className="size-1.5 animate-pulse rounded-full bg-emerald-500" />
                    {t('common:status.active')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : step === 0 ? (
          <div className="space-y-5">
            <h2 className="text-sm font-extrabold uppercase tracking-widest text-text-main">{t('onboarding.step0.heading')}</h2>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <FormField label={t('onboarding.step0.name')} placeholder={t('onboarding.step0.namePlaceholder')} value={org.name} onChange={(e) => setOrg({ ...org, name: (e.target as HTMLInputElement).value })} />
              <FormField
                as="select"
                label={t('onboarding.step0.type')}
                value={org.type}
                onChange={(e) => setOrg({ ...org, type: (e.target as HTMLSelectElement).value })}
              >
                <option value="">{t('onboarding.step0.selectType')}</option>
                {orgTypeKeys.map((k) => (
                  <option key={k} value={k}>{t(`onboarding.step0.types.${k}`)}</option>
                ))}
              </FormField>
              <FormField label={t('onboarding.step0.website')} placeholder={t('onboarding.step0.websitePlaceholder')} type="url" value={org.website} onChange={(e) => setOrg({ ...org, website: (e.target as HTMLInputElement).value })} />
            </div>
            <FormField as="textarea" label={t('onboarding.step0.purpose')} placeholder={t('onboarding.step0.purposePlaceholder')} value={org.purpose} onChange={(e) => setOrg({ ...org, purpose: (e.target as HTMLTextAreaElement).value })} />
            <div className="flex justify-end">
              <button className="btn-primary" onClick={() => setStep(1)} disabled={!org.name || !org.type}>
                {t('common:actions.continue')}
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_forward</span>
              </button>
            </div>
          </div>
        ) : step === 1 ? (
          <div className="space-y-5">
            <h2 className="text-sm font-extrabold uppercase tracking-widest text-text-main">{t('onboarding.step1.heading')}</h2>
            <div className="rounded-xl border-2 border-primary bg-blue-50/50 p-5">
              <div className="flex items-center gap-3">
                <div className="flex size-12 items-center justify-center rounded-lg bg-primary">
                  <span className="material-symbols-outlined text-white" style={{ fontSize: 24 }}>badge</span>
                </div>
                <div>
                  <p className="text-sm font-bold text-text-main">{t('onboarding.step1.yourDid')}</p>
                  <p className="mt-1 break-all font-mono text-xs font-semibold text-primary">{generatedDid}</p>
                </div>
              </div>
              <div className="mt-4 space-y-2 text-xs text-text-muted">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-emerald-500" style={{ fontSize: 14 }}>check_circle</span>
                  {t('onboarding.step1.checks.doc')}
                </div>
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-emerald-500" style={{ fontSize: 14 }}>check_circle</span>
                  {t('onboarding.step1.checks.key')}
                </div>
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-emerald-500" style={{ fontSize: 14 }}>check_circle</span>
                  {t('onboarding.step1.checks.secure')}
                </div>
              </div>
            </div>
            <div className="flex justify-between">
              <button className="btn-secondary" onClick={() => setStep(0)}>
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_back</span>
                {t('common:actions.back')}
              </button>
              <button className="btn-primary" onClick={() => setStep(2)}>
                {t('common:actions.continue')}
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_forward</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            <h2 className="text-sm font-extrabold uppercase tracking-widest text-text-main">{t('onboarding.step2.heading')}</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {frameworkIds.map((id) => (
                <button
                  key={id}
                  onClick={() => setSelectedFramework(id)}
                  className={`rounded-xl border-2 p-5 text-left transition-all ${
                    selectedFramework === id ? 'border-primary bg-blue-50/50' : 'border-border-subtle hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-bold text-text-main">{t(`onboarding.frameworks.${id}.name`)}</p>
                    <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">{t(`onboarding.frameworks.${id}.region`)}</span>
                  </div>
                  <p className="mt-2 text-xs text-text-muted">{t(`onboarding.frameworks.${id}.description`)}</p>
                </button>
              ))}
            </div>
            <div className="flex justify-between">
              <button className="btn-secondary" onClick={() => setStep(1)}>
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_back</span>
                {t('common:actions.back')}
              </button>
              <button className="btn-primary" onClick={() => setSubmitted(true)} disabled={!selectedFramework}>
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>check_circle</span>
                {t('onboarding.step2.complete')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
